import { Button, CloseButton, NumberInput, Select, Text, TextInput } from '@mantine/core';
import type { PaymentAnchor, PaymentTrancheType } from '@api/mother-order';
import { type FC } from 'react';

import classes from './TermsRows.module.css';
import {
    ANCHOR_OPTIONS,
    DEFAULT_ROW,
    draftsSum,
    nextRow,
    sumsToHundred,
    type TrancheDraft,
    TYPE_OPTIONS,
} from './termsRows.model';

/**
 * The rows of one set of payment terms: what share is due, at which milestone, how many days
 * either side of it. The one editor for them - on an order and on a vendor's saved terms - so
 * a row means the same thing wherever it is typed.
 */
export const TermsRows: FC<{
    tranches: TrancheDraft[];
    onChange: (tranches: TrancheDraft[]) => void;
    /** Shown beside each row when the value it is a share of is known (an order's total). */
    amountFor?: (tranche: TrancheDraft) => string;
}> = ({ tranches, onChange, amountFor }) => {
    const update = (idx: number, patch: Partial<TrancheDraft>): void =>
        onChange(tranches.map((t, i) => (i === idx ? { ...t, ...patch } : t)));

    const onTypePicked = (idx: number, type: PaymentTrancheType): void => {
        const defaults = DEFAULT_ROW[type];
        update(idx, {
            type,
            label: tranches[idx].label || (defaults.label ?? ''),
            anchor: defaults.anchor ?? tranches[idx].anchor,
        });
    };

    const rowClass = `${classes.row} ${amountFor ? classes.withAmount : ''}`;
    const sum = draftsSum(tranches);
    const sumOk = sumsToHundred(tranches);

    return (
        <div className={classes.rows}>
            {tranches.length > 0 && (
                <div className={`${rowClass} ${classes.head}`}>
                    <span>TYPE</span>
                    <span>LABEL</span>
                    <span>%</span>
                    <span>DUE AT</span>
                    <span title="Days relative to the milestone. Positive = after (Dispatch +60 = 60-day credit). Negative = before (Dispatch -4 = due 4 days before dispatch).">
                        ± DAYS
                    </span>
                    <span className={classes.amount}>{amountFor ? 'AMOUNT' : ''}</span>
                    <span />
                </div>
            )}
            {tranches.map((t, idx) => (
                <div key={idx} className={rowClass}>
                    <Select
                        size="xs"
                        aria-label="Type"
                        data={TYPE_OPTIONS}
                        value={t.type}
                        allowDeselect={false}
                        onChange={(v) => v && onTypePicked(idx, v as PaymentTrancheType)}
                    />
                    <TextInput
                        size="xs"
                        aria-label="Label"
                        placeholder="Label"
                        value={t.label}
                        onChange={(e) => update(idx, { label: e.currentTarget.value })}
                    />
                    <NumberInput
                        size="xs"
                        aria-label="Percentage"
                        placeholder="%"
                        min={0}
                        max={100}
                        hideControls
                        suffix="%"
                        value={t.percentage}
                        onChange={(v) =>
                            update(idx, { percentage: typeof v === 'number' ? v : '' })
                        }
                    />
                    <Select
                        size="xs"
                        aria-label="Due at"
                        data={ANCHOR_OPTIONS}
                        value={t.anchor}
                        allowDeselect={false}
                        onChange={(v) => v && update(idx, { anchor: v as PaymentAnchor })}
                    />
                    <NumberInput
                        size="xs"
                        aria-label="Days before or after"
                        placeholder="0"
                        min={-365}
                        max={365}
                        hideControls
                        value={t.offsetDays}
                        onChange={(v) =>
                            update(idx, { offsetDays: typeof v === 'number' ? v : '' })
                        }
                    />
                    <span className={classes.amount}>{amountFor ? amountFor(t) : ''}</span>
                    <CloseButton
                        size="sm"
                        c="red"
                        aria-label="Remove this term"
                        onClick={() => onChange(tranches.filter((_, i) => i !== idx))}
                    />
                </div>
            ))}
            <div className={classes.foot}>
                <Button
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => onChange([...tranches, nextRow(tranches)])}
                >
                    + Add payment term
                </Button>
                {tranches.length > 0 && (
                    <Text size="11px" fw={600} className={sumOk ? classes.sumOk : classes.sumBad}>
                        Total {sum}% {sumOk ? '✓' : '— must equal 100%'}
                    </Text>
                )}
            </div>
        </div>
    );
};
